import React, { useState, useEffect } from 'react';
import {
    Container,
    Box,
    CircularProgress,
    Alert,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography as MuiTypography,
    useTheme
} from '@mui/material';
import { TextFields, Save, ExpandMore, Title, Description, Store } from '@mui/icons-material';
import { PortalScreenHeader } from '../../../../components/PortalScreenHeader';
import { PermissionWrapper } from '../../../../components/PermissionWrapper';
import { BasicButton } from '../../../../components/BasicButton';
import { useStoreConfig } from '../../../../contexts/StoreConfigContext';
import apiClient from '../../../../services/api/ApiClient';
import { fontService } from '../../../../services/FontService';
import { FontSelector } from './components/FontSelector';

interface FontSettings {
    primaryFont: string | null;
    secondaryFont: string | null;
    logoFont: string | null;
}

export const Typography: React.FC = () => {
    const theme = useTheme();
    const { reloadConfig } = useStoreConfig();

    const [fonts, setFonts] = useState<FontSettings>({
        primaryFont: null,
        secondaryFont: null,
        logoFont: null
    });
    const [originalFonts, setOriginalFonts] = useState<FontSettings>({
        primaryFont: null,
        secondaryFont: null,
        logoFont: null
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    // Accordion state - allow multiple open
    const [expandedPanels, setExpandedPanels] = useState<string[]>(['primary']);

    // Font section configuration
    const fontSections = [
        {
            key: 'primary',
            fontKey: 'primaryFont' as keyof FontSettings,
            label: 'Primary Font (Headings)',
            description: 'Used for titles, product names, and navigation',
            defaultFontLabel: 'Default (Neue Haas)',
            icon: <Title />,
        },
        {
            key: 'secondary',
            fontKey: 'secondaryFont' as keyof FontSettings,
            label: 'Secondary Font (Body)',
            description: 'Used for descriptions, prices, and general text',
            defaultFontLabel: 'Default (Neue Haas)',
            icon: <Description />,
        },
        {
            key: 'logo',
            fontKey: 'logoFont' as keyof FontSettings,
            label: 'Logo Font (Branding)',
            description: 'Used for the store logo and header name',
            defaultFontLabel: 'Default (Stalinist One)',
            icon: <Store />,
        }
    ];

    const handlePanelChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedPanels(prev =>
            isExpanded
                ? [...prev, panel]
                : prev.filter(p => p !== panel)
        );
    };

    // Load current font settings
    useEffect(() => {
        const loadFontSettings = async () => {
            try {
                const response = await apiClient.get<FontSettings>('/customization/fonts/settings');
                const settings = response.data;
                setFonts(settings);
                setOriginalFonts(settings);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to load font settings');
            } finally {
                setLoading(false);
            }
        };

        loadFontSettings();
    }, []);

    // Load Google Font stylesheets for previews
    useEffect(() => {
        const fontsToLoad = [fonts.primaryFont, fonts.secondaryFont, fonts.logoFont].filter(Boolean) as string[];
        fontsToLoad.forEach(fontName => {
            const existingLink = document.querySelector(`link[data-font-preview="${fontName}"]`);
            if (!existingLink) {
                const link = document.createElement('link');
                link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontName)}:wght@400;500;600;700&display=swap`;
                link.rel = 'stylesheet';
                link.setAttribute('data-font-preview', fontName);
                document.head.appendChild(link);
            }
        });
    }, [fonts]);

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        setSuccess(false);

        try {
            await apiClient.patch('/customization/fonts', fonts);
            
            // Load the Google Font stylesheets so they're available immediately
            const fontsToLoad = [fonts.primaryFont, fonts.secondaryFont, fonts.logoFont].filter(Boolean) as string[];
            await Promise.all(fontsToLoad.map(font => fontService.loadFont(font)));
            
            setSuccess(true);
            setOriginalFonts(fonts);
            await reloadConfig();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save fonts');
        } finally {
            setSaving(false);
        }
    };

    const hasChanges = JSON.stringify(fonts) !== JSON.stringify(originalFonts);

    if (loading) {
        return (
            <PermissionWrapper screen='typography' permission='view'>
                <Container maxWidth='xl' sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Container>
            </PermissionWrapper>
        );
    }

    return (
        <PermissionWrapper screen='typography' permission='view'>
            <Container
                component='main'
                maxWidth='xl'
                sx={{
                    height: '100%',
                    paddingX: { xs: 0, md: 2 },
                    paddingBottom: 4
                }}
            >
                <PortalScreenHeader
                    title='Typography'
                    subtitle='Customize fonts for your storefront'
                    icon={<TextFields />}
                    mobileActionIcon={saving ? <CircularProgress size={20} color='inherit' /> : <Save />}
                    onMobileActionClick={hasChanges && !saving ? handleSave : undefined}
                    actions={
                        <BasicButton
                            title={saving ? 'Saving...' : 'Save'}
                            onClick={handleSave}
                            disabled={saving || !hasChanges}
                            startIcon={saving ? <CircularProgress size={20} color='inherit' /> : <Save />}
                        />
                    }
                />

                {error && (
                    <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert severity='success' sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
                        Fonts updated successfully! Changes will appear after refreshing the storefront.
                    </Alert>
                )}

                {/* Font Sections */}
                {fontSections.map((section) => {
                    const currentFont = fonts[section.fontKey];

                    return (
                        <Accordion
                            key={section.key}
                            expanded={expandedPanels.includes(section.key)}
                            onChange={handlePanelChange(section.key)}
                            sx={{
                                backgroundColor: theme.palette.background.paper,
                                borderRadius: '8px !important',
                                '&:before': { display: 'none' },
                                mb: 2,
                                overflow: 'hidden',
                                boxShadow: 'none',
                                border: `1px solid ${theme.palette.divider}`,
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMore />}
                                sx={{
                                    minHeight: { xs: 56, sm: 64 },
                                    '& .MuiAccordionSummary-content': {
                                        my: { xs: 1.5, sm: 2 },
                                    },
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 40,
                                            height: 40,
                                            borderRadius: 2,
                                            backgroundColor: theme.palette.mode === 'dark'
                                                ? 'rgba(255, 255, 255, 0.08)'
                                                : 'rgba(0, 0, 0, 0.04)',
                                            color: theme.palette.primary.main,
                                        }}
                                    >
                                        {section.icon}
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <MuiTypography 
                                            variant='subtitle1' 
                                            fontWeight={600}
                                            sx={{ 
                                                lineHeight: 1.3,
                                                textAlign: 'left'
                                            }}
                                        >
                                            {section.label}
                                        </MuiTypography>
                                        <MuiTypography 
                                            variant='body2' 
                                            color='text.secondary'
                                            sx={{ 
                                                textAlign: 'left',
                                                display: { xs: 'none', sm: 'block' }
                                            }}
                                        >
                                            {section.description}
                                        </MuiTypography>
                                    </Box>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 0 }}>
                                <FontSelector
                                    label={section.label}
                                    description={section.description}
                                    selectedFont={currentFont}
                                    onFontChange={(font) => setFonts(prev => ({ ...prev, [section.fontKey]: font }))}
                                    defaultFontLabel={section.defaultFontLabel}
                                    hideHeader
                                />
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
            </Container>
        </PermissionWrapper>
    );
};

export default Typography;
